import { Palette, chartPalette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIDataPoint,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { datetimeXAxis } from '../utils/constants'
import { capitalizeString } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig, commonChartOptions } from './common'

interface CandleStick {
  name: string
  values: {
    date: string
    value: number
  }[]
}

type Tag =
  | 'Tipo'
  | 'DataInicio'
  | 'DataFim'
  | 'ValorInicial'
  | 'ValorFinal'
  | 'Projection'

interface Linha {
  tag: Tag
  text: string | number
  linha: number
}

interface BoxAltasBaixas {
  tag: Tag
  text: string | number
  box: number
}

interface CompassHistoricalUsIbovChartData extends CompassAPIDataBase {
  title: string
  boxAltasBaixas: BoxAltasBaixas[]
  candleSticks: CandleStick[]
  linhas: Linha[]
}

interface CustomColumnPoint {
  last_value: number
  mean: number
  mean_minus_std: number
  mean_plus_std: number
  multiple: string
  sector: string
}

export interface CustomColumnChartData {
  data: CustomColumnPoint[]
}

const firstLetterToLowerCase = (input: string) =>
  input.charAt(0).toLowerCase() + input.slice(1)

export namespace ValuationReport {
  export namespace Valuation {
    const CONTEXT_ID = 'equity:valuation_report:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      title: string
      chart:
        | 'ibov_ex_ogx_pe_harmonic_mean'
        | 'ibov_ex_ogx_pe_median'
        | 'ibov_ex_ogx_ps_harmonic_mean'
        | 'ibov_ex_ogx_ps_median'
        | 'ibov_ex_ogx_pb_harmonic_mean'
        | 'ibov_ex_ogx_pb_median'
        | 'domesticos_pe_harmonic_mean'
        | 'domesticos_pe_median'
        | 'pe_ibov_domesticos_ex_banks'
        | 'pe_expanded_ibov_domestic_cyclical'
        | 'pe_expanded_ibov_domestic_defensive'
        | 'commodities_pe_harmonic_mean'
        | 'commodities_pe_median'
        | 'pe_ibov_commodities_ex_ogx'
        | 'commodities_bellwether_ev_to_ebitda_harmonic_mean'
        | 'commodities_bellwether_ev_to_ebitda_median'
        | 'ev_ebitda_expanded_ibov_commodities'
        | 'bellwether_pe_harmonic_mean'
        | 'bellwether_pe_median'
        | 'banks_pe_harmonic_mean'
        | 'banks_pe_median'
        | 'irr_itub'
        | 'pe_ratio_brazilian_em_banks'
        | 'rent3_peg_median'
        | 'lren3_peg_median'
        | 'expanded_ibov_z_score_region'
        | 'expanded_ibov_mixed_z_score_median'
        | 'pe_per_sector'
        | 'ev_ebitda_per_sector'
      context: typeof CONTEXT_ID
    }

    export type ChartData =
      | CompassAPIMultiSeriesChartData
      | CustomColumnChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'pe_ratio_brazilian_em_banks':
            return getRatioChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'expanded_ibov_z_score_region':
            return getStackedChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'pe_per_sector':
          case 'ev_ebitda_per_sector':
            return getColumnChartProps(config, data as CustomColumnChartData)
          default:
            return getMultipleLineChartPropsWrapper(
              config,
              data as CompassAPIMultiSeriesChartData
            )
        }
      },
    }

    const getRatioChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          msdIndicatorsSeriesIndexes: [0],
          msdIndicators: { avg: true, sd1: true, sd2: true },
          yAxis: [
            { height: '60%', id: 'primary', title: { text: '' } },
            { height: '40%', id: 'secondary', title: { text: '' }, top: '60%' },
          ],
        },
        { yAxis: ['primary', 'secondary', 'secondary'] }
      )(config, data)

    const getStackedChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          chartTitle: config.title,
          plotOptions: { area: { stacking: 'normal' } },
        },
        { type: Array(data.data.length).fill('area') }
      )(config, data)

    const getColumnChartProps = (
      config: ChartConfig,
      data: CustomColumnChartData
    ) => {
      const series = [
        'last_value',
        'mean',
        'mean_minus_std',
        'mean_plus_std',
      ].map((key, idx) => ({
        id: `series-${idx}`,
        type:
          key === 'mean' || key === 'mean_minus_std' || key === 'mean_plus_std'
            ? 'line'
            : 'column',
        name: capitalizeString(key).replaceAll('_', ' '),
        data: data.data.map((point: CustomColumnPoint) => [
          key,
          point[key as keyof CustomColumnPoint],
        ]),
        dataLabels: { enabled: true, format: '{y:.2f}' },
        ...(key === 'mean' ||
        key === 'mean_minus_std' ||
        key === 'mean_plus_std'
          ? {
              lineWidth: 0,
              marker: { enabled: true, radius: 2 },
              states: { hover: { lineWidthPlus: 0 } },
            }
          : {}),
        showInLegend: false,
        yAxis: 'default',
      }))

      const tooltip = { shared: true, split: undefined, valueDecimals: 2 }

      const xAxis = [{ categories: data.data.map(({ sector }) => sector) }]

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          ...(config.chart === 'pe_per_sector' ? { max: 50, min: 0 } : {}),
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: {
          ...commonChartOptions,
          credits: { text: 'Fonte: JGP' },
          series,
          stockTools: { gui: { enabled: false } },
          tooltip,
          xAxis,
          yAxis,
        },
        stockTools: false,
        useRegularChart: true,
      }
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: config.title,
          msdIndicators:
            config.chart === 'irr_itub'
              ? { avg: false, sd1: false, sd2: false }
              : { avg: true, sd1: true, sd2: true },
        },
        { name: config.chart === 'irr_itub' ? ['Ratio'] : [''] }
      )(config, data)
  }

  export namespace Performance {
    const CONTEXT_ID = 'equity:valuation_report:performance'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'ibovespa_em_usd' | 'methodologies'
      context: typeof CONTEXT_ID
      title: string
    }

    export type ChartData =
      | CompassHistoricalUsIbovChartData
      | CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ibovespa_em_usd':
            return getChartProps(
              config,
              data as CompassHistoricalUsIbovChartData
            )
          case 'methodologies':
            return getMultiSeriesChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
        }
      },
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassHistoricalUsIbovChartData
    ) => {
      const mainSeries = [
        {
          data: data.candleSticks[0].values.map((s, idx) => {
            const openValue = data.candleSticks[3].values[idx].value
            const closeValue = data.candleSticks[2].values[idx].value
            const baseDate = s.date.split('T')[0]
            return {
              x: new Date(baseDate).getTime(),
              high: s.value,
              low: data.candleSticks[1].values[idx].value,
              close: closeValue,
              open: openValue,
              name: `${baseDate.split('-')[1]}/${baseDate.split('-')[0]}`,
              color:
                openValue - closeValue > 0 ? Palette.darkGreen : Palette.orange,
            }
          }),
          dataGrouping: {
            forced: true,
            groupPixelWidth: 0,
            units: [['month', [...new Array(12)].map((_, idx) => idx + 1)]],
          },
          id: 'main-series',
          name: 'Historical US IBOV',
          type: 'candlestick',
          yAxis: 'default',
        },
      ]

      const getCycleSeries = (data: BoxAltasBaixas[]) => {
        const numberOfCycles = data.filter(
          (el, idx, self) => self.findIndex((v) => v.box === el.box) === idx
        ).length

        const getCycleSeries = (tipo: 'uptrend' | 'downtrend') => {
          const cycleType = tipo === 'uptrend' ? 'Box Azul' : 'Box Vermelho'
          return {
            color: tipo === 'uptrend' ? 'rgb(22,22,77)' : 'rgb(48,168,9)',
            data: [...new Array(numberOfCycles)]
              .map((_, i) => i + 1)
              .map((box) =>
                Object.assign(
                  {},
                  ...data
                    .filter((point) => point.box === box)
                    .map((el) => ({
                      [firstLetterToLowerCase(el.tag)]: el.text,
                    }))
                )
              )
              .filter((cycle) => cycle.tipo === cycleType)
              .map(({ dataInicio, dataFim, valorInicial, valorFinal }) => [
                [new Date(dataInicio).getTime(), valorInicial],
                [new Date(dataFim).getTime(), valorInicial],
                [new Date(dataFim).getTime(), valorFinal],
                [new Date(dataInicio).getTime(), valorFinal],
                null,
              ])
              .flat(),
            enableMouseTracking: false,
            id: `cycle-series-${cycleType}`,
            name: `${capitalizeString(tipo)} cycles`,
            opacity: 0.5,
            type: 'polygon',
            visible: false,
            yAxis: 'default',
          }
        }

        return [getCycleSeries('uptrend'), getCycleSeries('downtrend')]
      }

      const getTrendSeries = (data: Linha[]) => {
        const getTrendData = (line: Linha[], idx: number) => [
          {
            date: line
              .filter(({ linha }) => linha === idx)
              .filter(({ tag }) => tag === 'DataInicio')[0].text,
            value: line
              .filter((s) => s.linha === idx)
              .filter(({ tag }) => tag === 'ValorInicial')[0].text,
          },
          {
            date: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            value: line
              .filter(({ linha }) => linha === idx)
              .filter(({ tag }) => tag === 'Projection')[0].text,
          },
        ]

        return ['upper-tendency', 'lower-tendency']
          .map((trend, idx) => ({
            id: trend,
            data: getTrendData(data, idx + 1),
          }))
          .map((s) => ({
            color: Palette.gray,
            data: convertCompassToHighchartsDataFormat(
              s.data as CompassAPIDataPoint[]
            ),
            dashStyle: s.id === 'upper-tendency' ? 'LongDash' : 'Solid',
            id: s.id,
            tooltip: { valueDecimals: 0, xDateFormat: '%B %Y' },
            showInLegend: false,
            yAxis: 'default',
          }))
      }

      const trendSeries = getTrendSeries(data.linhas)

      const lastPointFlags = [mainSeries[0].data, trendSeries[1].data].map(
        (series, idx) => {
          const getLastValue = (s: any) =>
            idx === 0 ? s[s.length - 1].close : s[s.length - 1][1]

          const getLastDate = (s: any) =>
            idx === 0 ? s[s.length - 1].x : s[s.length - 1][0]

          const getLastValueLabel = (s: any) =>
            (getLastValue(s) / 1000).toFixed(2) + 'k'

          return {
            data: [
              {
                x: getLastDate(series),
                text: getLastValueLabel(series),
                title: getLastValueLabel(series),
              },
            ],
            id: `series-last-point-${idx}`,
            onSeries: idx === 0 ? 'main-series' : 'lower-tendency',
            shape: 'squarepin',
            showInLegend: false,
            type: 'flags',
            y: 50 * (idx === 0 ? -1 : 1),
            yAxis: 'default',
          }
        }
      )

      const series = [
        ...mainSeries,
        ...getCycleSeries(data.boxAltasBaixas),
        ...trendSeries,
        ...lastPointFlags,
      ]

      const yAxis = [
        {
          id: 'default',
          opposite: true,
          type: 'logarithmic',
          reversed: false,
          offset: 50,
          tickInterval: 0.4,
          labels: { style: { fontSize: '18px' } },
        },
      ]

      return {
        id: Context.getChartId(config),
        height: '700px',
        options: mergeDeepAll([
          commonChartOptions,
          getDataSources(data.data_sources),
          {
            chart: {
              backgroundColor: 'white',
              style: {
                fontSize: '15px',
                fontFamily: '"Work Sans", sans-serif',
              },
            },
            title: {
              text: 'IBOVESPA em USD',
              style: { fontSize: 30 },
            },
            series,
            tooltip: { shared: true, split: false },
            xAxis: [
              { ...datetimeXAxis, labels: { style: { fontSize: '18px' } } },
            ],
            yAxis,
          },
        ]),
        stockTools: true,
      }
    }

    const getMultiSeriesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: config.title,
          convertToPercentages: Array(data.data.length).fill(true),
        },
        { name: ['Ibovespa', 'Ibovespa nova metodologia', 'S&P em BRL'] }
      )(config, data)
  }

  export namespace Yield {
    const CONTEXT_ID = 'equity:valuation_report:yield'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'dividend_yield_bf'
        | 'dividend_yield_juro_real'
        | 'ntnb_10y'
        | 'earnings_yield_juro_real'
      context: typeof CONTEXT_ID
      title: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartPropsWrapper(config, data),
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: Array(data.data.length).fill(true),
        chartTitle: config.title,
        msdIndicators: { avg: true, sd1: true, sd2: true },
      })(config, data)
  }

  export namespace MarketCap {
    const CONTEXT_ID = 'equity:valuation_report:market_cap'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'market_cap_br_gdp' | 'market_cap_br_world_exchange'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartPropsWrapper(config, data),
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          convertToPercentages: Array(data.data.length).fill(true),
          msdIndicators: { avg: false, sd1: false, sd2: false },
        },
        {
          color: [...chartPalette.slice(0, 2), ...chartPalette.slice(0, 2)],
          dashStyle: ['Solid', 'Solid', 'ShortDot', 'ShortDot'],
          showInLegend: [true, true, false, false],
        }
      )(config, data)
  }

  export namespace USEM {
    const CONTEXT_ID = 'equity:valuation_report:us_em'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'pe_em_us'
        | 'pe_br_em'
        | 'pe_domesticos_em'
        | 'pe_brasil_us'
        | 'pe_ratio_ibov_domesticos_vs_spy'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'pe_ratio_ibov_domesticos_vs_spy':
            return getPeRatioChartProps(config, data)
          default:
            return getMultipleLineChartPropsWrapper(config, data)
        }
      },
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        chartTitle: config.title,
        msdIndicators: { avg: true, sd1: true, sd2: true },
      })(config, data)

    const getPeRatioChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: config.title,
          msdIndicatorsSeriesIndexes: [0],
          msdIndicators: { avg: true, sd1: true, sd2: true },
          yAxis: [
            { height: '60%', id: 'primary', title: { text: '' } },
            { height: '40%', id: 'secondary', title: { text: '' }, top: '60%' },
          ],
        },
        {
          name: ['Ratio', 'Domestic IBOV', 'SPY'],
          yAxis: ['primary', 'secondary', 'secondary'],
        }
      )(config, data)
  }

  export namespace Upside {
    const CONTEXT_ID = 'equity:valuation_report:upside'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'ibov_potential_upside'
        | 'ibov_analyst_rating'
        | 'pb_ratio_bbas3_itub'
        | 'pb_ratio_1q_4q'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ibov_potential_upside':
          case 'ibov_analyst_rating':
            return getMultipleLineChartPropsWrapper(config, data)
          case 'pb_ratio_bbas3_itub':
            return getRatioChartProps(config, data, 'PB BBAS/ITUB', [
              'Ratio',
              'BBAS',
              'ITUB',
            ])
          case 'pb_ratio_1q_4q':
            return getRatioChartProps(config, data, 'PB IBOV-1Q/IBOV-4Q', [
              'Ratio',
              '1Q',
              '4Q',
            ])
        }
      },
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        chartTitle:
          config.chart === 'ibov_potential_upside'
            ? 'Potential Upside'
            : 'Consensus Rating',
        msdIndicators: { avg: true, sd1: true, sd2: true },
      })(config, data)

    const getRatioChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      title: string,
      seriesNames: string[]
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: title,
          msdIndicators: { avg: true, sd1: true, sd2: true },
          msdIndicatorsSeriesIndexes: [0],
          yAxis: [
            {
              height: '60%',
              id: 'primary',
              title: { text: '' },
            },
            {
              height: '40%',
              id: 'secondary',
              title: { text: '' },
              top: '60%',
            },
          ],
        },
        {
          name: seriesNames,
          yAxis: ['primary', 'secondary', 'secondary'],
        }
      )(config, data)
  }

  export namespace Technical {
    const CONTEXT_ID = 'equity:valuation_report:technical'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'ibov_breadth' | 'spy_breadth'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) => {
      const isBreadth = (name: string) =>
        name.toLocaleLowerCase().includes('breadth')
      return getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: config.title,
          tooltip: { pointFormatter: magnitudePointFormatter() },
          yAxis: [
            { id: 'primary', opposite: false },
            { id: 'secondary', opposite: true },
          ].map((axis) => ({ ...axis, startOnTick: false, endOnTick: false })),
        },
        {
          yAxis: data.data.map((value) =>
            isBreadth(value.name) ? 'secondary' : 'primary'
          ),
          name: data.data.map((value) =>
            isBreadth(value.name) ? 'Breadth' : value.name
          ),
        }
      )(config, data)
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }
}
